import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    GasAjaxError,
    GasAntiSpamField,
    GasDateField,
    GasFieldHelp,
    GasMembershipSteps,
    GasPhoneField,
    GasAddressField,
    GasSelect,
    type GasAddressValue,
    GasStepTimeline,
    getGasFormDisplayPreset,
    useGasI18n,
    type GasFieldHelpMode,
    type GasRequiredIndicatorMode,
    type GasStepTimelineStep,
} from '../../shared/kit';
import GasTurnstileField from './components/GasTurnstileField';
import { checkEmailAvailable, registerMember, verifyAccount } from './api';
import type { RegistrationApiConfig } from './api';
import type {
    FieldErrors,
    GeoCountry,
    MemberRegistrationAjaxResponse,
    MemberRegistrationInitData,
    MembershipMode,
    RegistrationFormState,
    RegistrationStep,
    VerificationFormState,
} from './types';

interface AppProps {
    initData: MemberRegistrationInitData;
}

const REGISTRATION_FORM_DISPLAY = getGasFormDisplayPreset('simple');

function todayLocalDate(): string {
    const now = new Date();
    const year = String(now.getFullYear());
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
}

function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
    const normalized = String(phone || '').replace(/[\s\-.\(\)]/g, '');
    return /^(?:\+|0)\d{8,15}$/.test(normalized);
}

function isValidDate(dateString: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return false;
    }

    const parts = dateString.split('-').map(Number);
    const date = new Date(parts[0], parts[1] - 1, parts[2]);

    return date.getFullYear() === parts[0] && date.getMonth() + 1 === parts[1] && date.getDate() === parts[2];
}

function extractServerErrors(error: unknown): { message: string; errors: FieldErrors } {
    // Erreur AJAX (GasAjaxError) : data est dans error.data
    if (error instanceof GasAjaxError && error.data && typeof error.data === 'object') {
        const data = error.data as { message?: string; errors?: FieldErrors };
        return {
            message: data.message || error.message,
            errors: data.errors || {},
        };
    }

    // Erreur REST : on enrichit Error avec .data contenant les erreurs de champs
    if (error instanceof Error) {
        const enriched = error as Error & { data?: { errors?: FieldErrors; message?: string } };
        if (enriched.data && typeof enriched.data === 'object') {
            return {
                message: enriched.data.message || error.message,
                errors: enriched.data.errors || {},
            };
        }
        return { message: error.message, errors: {} };
    }

    return {
        message: '',
        errors: {},
    };
}

function FormError({ message }: { message?: string }) {
    return message ? <p className="error-message">{message}</p> : null;
}

function RegFieldLabel({
    htmlFor,
    label,
    required = false,
    help,
    helpMode = REGISTRATION_FORM_DISPLAY.fieldHelpMode,
    requiredIndicator = REGISTRATION_FORM_DISPLAY.requiredIndicator,
    requiredLabel,
    className,
}: {
    htmlFor?: string;
    label: React.ReactNode;
    required?: boolean;
    help?: string;
    helpMode?: GasFieldHelpMode;
    requiredIndicator?: GasRequiredIndicatorMode;
    requiredLabel?: string;
    className?: string;
}) {
    const { t } = useGasI18n();
    const resolvedRequiredLabel = requiredLabel || t('membre.profile.field_required_badge');

    return (
        <label htmlFor={htmlFor} className={className}>
            {label}
            {required && requiredIndicator === 'badge' ? (
                <span className="gas-registration-required-badge">{resolvedRequiredLabel}</span>
            ) : null}
            {required && requiredIndicator === 'asterisk' ? <span className="required"> *</span> : null}
            <GasFieldHelp
                text={help}
                mode={helpMode}
                inlineClassName="gas-registration-field-help-inline"
            />
        </label>
    );
}

/** Encadré de conseils d'une étape du wizard d'inscription. */
function RegStepGuidance({ stepKey }: { stepKey: string }) {
    const { t } = useGasI18n();
    const base = 'membre.inscription.wizard_guide_' + stepKey;
    const tip = t(base + '_tip');
    const hasTip = tip !== base + '_tip';
    return (
        <div className="gas-reg-wizard__guide">
            <h3 className="gas-reg-wizard__guide-title">{t(base + '_title')}</h3>
            <p className="gas-reg-wizard__guide-body">{t(base + '_body')}</p>
            {hasTip ? (
                <p className="gas-reg-wizard__guide-tip"><span aria-hidden="true">💡</span> {tip}</p>
            ) : null}
        </div>
    );
}

export default function App({ initData }: AppProps) {
    const { t } = useGasI18n();
    const config = initData.config || {};
    const texts = initData.texts || {};
    const steps = Array.isArray(initData.steps) ? initData.steps : [];
    const isLoggedIn = !!initData.isLoggedIn;
    const hasMemberAccount = !!initData.hasMemberAccount;
    const userInfo = initData.userInfo || {};
    const existingMemberEditUrl = initData.existingMemberEditUrl || '';
    const geoCfg = config.geo || {};
    const geoCountries: GeoCountry[] = Array.isArray(geoCfg.countries) && geoCfg.countries.length
        ? geoCfg.countries
        : [{ code: 'ch', label: 'Suisse' }];
    const geoDefault = geoCfg.default || geoCountries[0].code;
    // Mode « simple » (inscription minimale comme WordPress) : on ne demande que
    // prénom, nom, genre et email. Le dossier professionnel (téléphone, formation,
    // séances, adresse + carte, engagement cotisation) est masqué et non requis.
    const membershipMode: MembershipMode = (config.membership?.mode || 'certificate') as MembershipMode;
    const isSimpleMode = membershipMode === 'simple';
    const isImmediateMode = membershipMode === 'simple' || membershipMode === 'auto';
    const isApprovalMode = membershipMode === 'approval';
    // Aligné sur gas_membership_cotisation_enabled() — si false, pas de case engagement.
    const cotisationEnabled = config.membership?.cotisation_enabled !== false;
    // Mode approval : téléphone optionnel (candidature low-friction), aligné serveur.
    const phoneOptional = isApprovalMode;
    // Aligné sur gas_address_registration_fill_mode('correspondance') côté serveur.
    const addressFillMode = String(
        config.address_policy?.registration_fill_mode?.correspondance || 'optional',
    );
    const addressAllRequired = addressFillMode === 'all_required';
    const addressCountryRequired = addressFillMode === 'country_required' || addressAllRequired;
    const addressStepVisible = addressFillMode !== 'hidden';
    const addressOptional = addressFillMode === 'optional' || addressFillMode === 'country_required';
    const showPaysSelect = geoCountries.length > 1;
    const privacyUrl = String(config.privacy_url || '').trim();
    const selectPlaceholder = t('membre.profile.select_placeholder');
    const allowedGenders = useMemo<string[]>(function () {
        const fromConfig = config.membership?.gender_options;
        if (Array.isArray(fromConfig) && fromConfig.length > 0) {
            return fromConfig as string[];
        }
        return ['homme', 'femme', 'non_indique'];
    }, [config.membership]);
    const genreOptions = useMemo(function () {
        const labels: Record<string, string> = {
            femme: t('membre.profile.genre_femme'),
            homme: t('membre.profile.genre_homme'),
            non_binaire: t('membre.profile.genre_non_binaire'),
            non_indique: t('membre.profile.genre_non_indique'),
        };
        return allowedGenders
            .filter((g) => labels[g])
            .map((g) => ({ value: g, label: labels[g] }));
    }, [t, allowedGenders]);
    const verificationFormationOptions = useMemo(function () {
        return [
            { value: '1', label: t('membre.inscription.reg_formation_base_oui_simple') },
            { value: '0', label: t('membre.inscription.reg_formation_base_non_simple') },
        ];
    }, [t]);
    const registrationFormationOptions = useMemo(function () {
        return [
            { value: '1', label: t('membre.inscription.reg_formation_base_oui') },
            { value: '0', label: t('membre.inscription.reg_formation_base_non') },
        ];
    }, [t]);
    const countryOptions = useMemo(function () {
        return geoCountries.map(function (country) {
            return { value: country.code, label: country.label };
        });
    }, [geoCountries]);
    const successRef = useRef<HTMLDivElement | null>(null);
    const appRef = useRef<HTMLDivElement | null>(null);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [successProfileUrl, setSuccessProfileUrl] = useState('');
    const [successEmailSent, setSuccessEmailSent] = useState(true);
    const [successEmailQueued, setSuccessEmailQueued] = useState(false);
    const [globalError, setGlobalError] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    // Anti-bot : Turnstile si activé/configuré côté serveur, sinon challenge math.
    const turnstileActive = !!config.turnstile?.active && !!config.turnstile?.site_key;
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
    // Vérification email live (GET /inscription/email-available).
    const [emailCheckStatus, setEmailCheckStatus] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'invalid'>('idle');

    // --- Inscription guidée « pas à pas » (wizard).
    // Étapes : identity → (contact → practice → [address] → [engagement]) → summary → validation
    const [regStep, setRegStep] = useState(0);
    const regStepKeys = useMemo<string[]>(function () {
        if (isSimpleMode) {
            return ['identity', 'summary', 'validation'];
        }
        const steps = ['identity', 'contact', 'practice'];
        if (addressStepVisible) {
            steps.push('address');
        }
        if (cotisationEnabled) {
            steps.push('engagement');
        }
        steps.push('summary');
        steps.push('validation');
        return steps;
    }, [isSimpleMode, addressStepVisible, cotisationEnabled]);
    // Étapes déjà ouvertes (courante ou revisitée). Sert à la frise : un ✓ ne doit
    // jamais apparaître au chargement pour une étape jamais vue — surtout si elle
    // est optionnelle (aucune erreur de validation = faux positif « fait »).
    const [visitedRegSteps, setVisitedRegSteps] = useState<Set<string>>(() => new Set());
    useEffect(function () {
        const key = regStepKeys[regStep];
        if (!key) {
            return;
        }
        setVisitedRegSteps(function (prev) {
            if (prev.has(key)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    }, [regStep, regStepKeys]);
    const [registrationForm, setRegistrationForm] = useState<RegistrationFormState>({
        prenom: '',
        nom: '',
        genre: '',
        email: '',
        date_naissance: '',
        telephone: '',
        pratique_depuis: todayLocalDate(),
        date_nombre_seances: todayLocalDate(),
        nombre_seances_donnees: 0,
        adresse_correspondance: '',
        npa_correspondance: '',
        localite_correspondance: '',
        pays_correspondance: geoDefault,
        latitude_correspondance: '',
        longitude_correspondance: '',
        adresse_covoiturage: 1,
        en_formation_base: '',
        accepte_cotisation_statuts: false,
        website: '',
        math_answer: '',
    });
    const [verificationForm, setVerificationForm] = useState<VerificationFormState>({
        genre: '',
        en_formation_base: '',
        website: '',
        math_answer: '',
    });

    const geoAllowedLabel = geoCountries.map(function (country) { return country.label; }).join(', ');
    const geoInfoMessage = geoCountries.length === 1
        ? t('membre.inscription.geo_single_country', { country: geoCountries[0].label })
        : t('membre.inscription.geo_multi_country', { countries: geoAllowedLabel });

    // Étape courante du parcours selon le contexte : visiteur sur le formulaire
    // (étape 1) ; écran de succès (compte créé/migré → étape 2 ou 3).
    // Modes immédiats : 3 étapes max → succès à l'étape 2 (email) ou 3 (accès).
    const successCurrentStep = isLoggedIn
        ? (isImmediateMode ? 3 : 3)
        : 2;

    const apiCfg: RegistrationApiConfig = {
        ajaxUrl:        config.ajaxUrl,
        nonce:          config.nonce,
        rest_nonce:     config.rest_nonce,
        rest_base_url:  config.rest_base_url,
        api_transport:  config.api_transport,
    };

    const registrationMutation = useMutation({
        mutationFn(nextForm: RegistrationFormState) {
            return registerMember(apiCfg, {
                prenom: nextForm.prenom,
                nom: nextForm.nom,
                genre: nextForm.genre,
                email: nextForm.email,
                date_naissance: nextForm.date_naissance || '',
                telephone: nextForm.telephone || '',
                pratique_depuis: nextForm.pratique_depuis || '',
                date_nombre_seances: nextForm.date_nombre_seances || '',
                nombre_seances_donnees: nextForm.nombre_seances_donnees !== '' ? String(nextForm.nombre_seances_donnees) : '',
                adresse_correspondance: nextForm.adresse_correspondance,
                npa_correspondance: nextForm.npa_correspondance,
                localite_correspondance: nextForm.localite_correspondance,
                pays_correspondance: nextForm.pays_correspondance || geoDefault,
                latitude_correspondance: nextForm.latitude_correspondance,
                longitude_correspondance: nextForm.longitude_correspondance,
                adresse_covoiturage: String(nextForm.adresse_covoiturage),
                en_formation_base: nextForm.en_formation_base,
                accepte_cotisation_statuts: nextForm.accepte_cotisation_statuts ? '1' : '0',
                website: nextForm.website,
                math_answer: String(nextForm.math_answer),
                math_challenge_id: config.mathChallengeId || '',
                'cf-turnstile-response': turnstileActive ? turnstileToken : '',
            });
        },
        onSuccess(response: MemberRegistrationAjaxResponse) {
            setSuccessEmailSent(response.email_sent !== false);
            setSuccessEmailQueued(response.email_queued === true);
            setRegistrationSuccess(true);
            scrollToSuccess();
        },
        onError(error) {
            const server = extractServerErrors(error);
            setErrors(server.errors);
            setGlobalError(server.message || t('membre.inscription.error_server_generic'));
            scrollToFirstError();
        },
    });

    const verificationMutation = useMutation({
        mutationFn(nextForm: VerificationFormState) {
            return verifyAccount(apiCfg, {
                genre: nextForm.genre,
                en_formation_base: nextForm.en_formation_base,
                website: nextForm.website,
                math_answer: String(nextForm.math_answer),
                math_challenge_id: config.mathChallengeId || '',
                'cf-turnstile-response': turnstileActive ? turnstileToken : '',
            });
        },
        onSuccess(response) {
            setSuccessProfileUrl(response.profile_url || '');
            setSuccessEmailSent(true);
            setRegistrationSuccess(true);
            scrollToSuccess();
        },
        onError(error) {
            const server = extractServerErrors(error);
            setErrors(server.errors);
            setGlobalError(server.message || t('membre.inscription.error_server_retry'));
            scrollToFirstError();
        },
    });

    const isSubmitting = registrationMutation.isPending || verificationMutation.isPending;

    useEffect(function () {
        if (registrationForm.en_formation_base === '1') {
            setRegistrationForm(function (current) {
                const today = todayLocalDate();
                return {
                    ...current,
                    pratique_depuis: today,
                    date_nombre_seances: today,
                    nombre_seances_donnees: 0,
                };
            });
            setErrors(function (current) {
                const next = { ...current };
                delete next.pratique_depuis;
                delete next.date_nombre_seances;
                delete next.nombre_seances_donnees;
                return next;
            });
        }
    }, [registrationForm.en_formation_base]);

    // Vérification email live (debounce 450 ms) — évite le mur en fin de parcours.
    useEffect(function () {
        const email = registrationForm.email.trim();
        if (!email) {
            setEmailCheckStatus('idle');
            return;
        }
        if (!isValidEmail(email)) {
            setEmailCheckStatus('invalid');
            return;
        }
        // Sans REST (nonce), on ne peut pas appeler l'endpoint — rester idle.
        if (!apiCfg.rest_nonce && apiCfg.api_transport === 'ajax') {
            setEmailCheckStatus('idle');
            return;
        }

        let cancelled = false;
        setEmailCheckStatus('checking');
        const timer = window.setTimeout(function () {
            checkEmailAvailable(apiCfg, email)
                .then(function (res) {
                    if (cancelled) {
                        return;
                    }
                    if (!res.valid) {
                        setEmailCheckStatus('invalid');
                        return;
                    }
                    setEmailCheckStatus(res.available ? 'ok' : 'taken');
                    if (!res.available) {
                        setErrors(function (current) {
                            return {
                                ...current,
                                email: res.message || t('membre.inscription.error_email_exists'),
                            };
                        });
                    } else {
                        setErrors(function (current) {
                            if (!current.email) {
                                return current;
                            }
                            const next = { ...current };
                            delete next.email;
                            return next;
                        });
                    }
                })
                .catch(function () {
                    if (!cancelled) {
                        setEmailCheckStatus('idle');
                    }
                });
        }, 450);

        return function () {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // apiCfg est stable pour la session (init) ; email est le trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registrationForm.email]);

    function updateRegistrationField<K extends keyof RegistrationFormState>(field: K, value: RegistrationFormState[K]) {
        setRegistrationForm(function (current) {
            return { ...current, [field]: value };
        });
    }

    function updateVerificationField<K extends keyof VerificationFormState>(field: K, value: VerificationFormState[K]) {
        setVerificationForm(function (current) {
            return { ...current, [field]: value };
        });
    }

    function handleTurnstileToken(token: string) {
        setTurnstileToken(token);
        if (token) {
            setErrors(function (current) {
                const next = { ...current };
                delete next.math_answer;
                return next;
            });
        }
    }

    // Adresse de correspondance : GasAddressField (autocomplete + carte sans
    // clé) écrit les 6 champs d'un coup et lève les erreurs correspondantes.
    function onAddressChange(next: GasAddressValue) {
        setRegistrationForm(function (current) {
            return {
                ...current,
                adresse_correspondance: next.address,
                npa_correspondance: next.npa,
                localite_correspondance: next.localite,
                pays_correspondance: next.pays || current.pays_correspondance,
                latitude_correspondance: next.latitude,
                longitude_correspondance: next.longitude,
            };
        });
        setErrors(function (current) {
            const nextErrors = { ...current };
            delete nextErrors.adresse_correspondance;
            delete nextErrors.npa_correspondance;
            delete nextErrors.localite_correspondance;
            delete nextErrors.pays_correspondance;
            delete nextErrors.latitude_correspondance;
            delete nextErrors.longitude_correspondance;
            return nextErrors;
        });
    }

    // Erreur anti-bot : Turnstile (token requis) si actif, sinon challenge math.
    function antiBotError(mathValue: string | number): string {
        if (turnstileActive) {
            if (turnstileToken) {
                return '';
            }
            return turnstileUnavailable
                ? t('membre.inscription.error_turnstile_unavailable')
                : t('membre.inscription.error_turnstile_required');
        }
        if (mathValue === '' || Number(mathValue) !== Number(config.mathA || 0) + Number(config.mathB || 0)) {
            return t('membre.inscription.error_math_invalid');
        }
        return '';
    }

    function validateRegistrationForm(form: RegistrationFormState = registrationForm): boolean {
        const nextErrors: FieldErrors = {};

        if (!form.prenom.trim()) {
            nextErrors.prenom = t('membre.inscription.error_prenom_required');
        }
        if (!form.nom.trim()) {
            nextErrors.nom = t('membre.inscription.error_nom_required');
        }
        if (!form.genre) {
            nextErrors.genre = t('membre.inscription.error_genre_required');
        }
        if (!form.email.trim()) {
            nextErrors.email = t('membre.inscription.error_email_required');
        } else if (!isValidEmail(form.email)) {
            nextErrors.email = t('membre.inscription.error_email_invalid');
        }
        if (form.date_naissance && !isValidDate(form.date_naissance)) {
            nextErrors.date_naissance = t('membre.inscription.error_date_naissance_invalid');
        }

        if (emailCheckStatus === 'taken') {
            nextErrors.email = t('membre.inscription.error_email_exists');
        }

        // Mode simple : seuls identité + email (+ anti-bot) sont requis.
        if (isSimpleMode) {
            const abErr = antiBotError(form.math_answer);
            if (abErr) {
                nextErrors.math_answer = abErr;
            }
            setErrors(nextErrors);
            return Object.keys(nextErrors).length === 0;
        }

        if (!form.telephone.trim()) {
            if (!phoneOptional) {
                nextErrors.telephone = t('membre.inscription.error_telephone_required');
            }
        } else if (!isValidPhone(form.telephone)) {
            nextErrors.telephone = t('membre.inscription.error_telephone_invalid');
        }
        // Adresse : miroir de validateAddressByFillMode() PHP (site-config address_policy).
        if (addressAllRequired) {
            if (!form.adresse_correspondance.trim()) {
                nextErrors.adresse_correspondance = t('membre.inscription.error_adresse_required');
            }
            if (!form.npa_correspondance.trim()) {
                nextErrors.npa_correspondance = t('membre.inscription.error_npa_required');
            }
            if (!form.localite_correspondance.trim()) {
                nextErrors.localite_correspondance = t('membre.inscription.error_localite_required');
            }
            if (!form.pays_correspondance.trim()) {
                nextErrors.pays_correspondance = t('membre.inscription.pays_required');
            }
        } else if (addressCountryRequired) {
            // Pays prérempli (défaut geo) ne compte pas comme « adresse commencée ».
            const anyAddr = !!(
                form.adresse_correspondance.trim()
                || form.npa_correspondance.trim()
                || form.localite_correspondance.trim()
            );
            if (anyAddr && !form.pays_correspondance.trim()) {
                nextErrors.pays_correspondance = t('membre.inscription.pays_required');
            }
        }
        // GPS non bloquant : coordonnées via GasAddressField ; saisie manuelle OK.
        if (form.en_formation_base === '') {
            nextErrors.en_formation_base = t('membre.inscription.error_formation_base_required');
        }
        if (form.en_formation_base === '0') {
            if (!form.pratique_depuis) {
                nextErrors.pratique_depuis = t('membre.inscription.error_pratique_depuis_required');
            } else if (!isValidDate(form.pratique_depuis)) {
                nextErrors.pratique_depuis = t('membre.inscription.error_pratique_depuis_invalid');
            }
            if (!form.date_nombre_seances) {
                nextErrors.date_nombre_seances = t('membre.inscription.error_date_seances_required');
            } else if (!isValidDate(form.date_nombre_seances)) {
                nextErrors.date_nombre_seances = t('membre.inscription.error_date_seances_invalid');
            }
            const nombreSeances = Number(form.nombre_seances_donnees);
            if (form.nombre_seances_donnees === '' || Number.isNaN(nombreSeances) || nombreSeances < 0) {
                nextErrors.nombre_seances_donnees = t('membre.inscription.error_nombre_seances_invalid');
            }
        }
        if (cotisationEnabled && !form.accepte_cotisation_statuts) {
            nextErrors.accepte_cotisation_statuts = t('membre.inscription.error_cotisation_required');
        }
        const abErr = antiBotError(form.math_answer);
        if (abErr) {
            nextErrors.math_answer = abErr;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }

    /**
     * Validation d'UNE étape du wizard (sous-ensemble de validateRegistrationForm).
     * Le submit final repasse par validateRegistrationForm (gate autoritaire).
     */
    function stepFieldErrors(stepKey: string, form: RegistrationFormState): FieldErrors {
        const e: FieldErrors = {};
        if (stepKey === 'identity') {
            if (!form.prenom.trim()) e.prenom = t('membre.inscription.error_prenom_required');
            if (!form.nom.trim()) e.nom = t('membre.inscription.error_nom_required');
            if (!form.genre) e.genre = t('membre.inscription.error_genre_required');
            if (!form.email.trim()) e.email = t('membre.inscription.error_email_required');
            else if (!isValidEmail(form.email)) e.email = t('membre.inscription.error_email_invalid');
            else if (emailCheckStatus === 'taken') e.email = t('membre.inscription.error_email_exists');
        } else if (stepKey === 'contact') {
            if (!form.telephone.trim()) {
                if (!phoneOptional) e.telephone = t('membre.inscription.error_telephone_required');
            } else if (!isValidPhone(form.telephone)) {
                e.telephone = t('membre.inscription.error_telephone_invalid');
            }
            if (form.date_naissance && !isValidDate(form.date_naissance)) e.date_naissance = t('membre.inscription.error_date_naissance_invalid');
        } else if (stepKey === 'practice') {
            if (form.en_formation_base === '') e.en_formation_base = t('membre.inscription.error_formation_base_required');
            if (form.en_formation_base === '0') {
                if (!form.pratique_depuis) e.pratique_depuis = t('membre.inscription.error_pratique_depuis_required');
                else if (!isValidDate(form.pratique_depuis)) e.pratique_depuis = t('membre.inscription.error_pratique_depuis_invalid');
                if (!form.date_nombre_seances) e.date_nombre_seances = t('membre.inscription.error_date_seances_required');
                else if (!isValidDate(form.date_nombre_seances)) e.date_nombre_seances = t('membre.inscription.error_date_seances_invalid');
                const n = Number(form.nombre_seances_donnees);
                if (form.nombre_seances_donnees === '' || Number.isNaN(n) || n < 0) e.nombre_seances_donnees = t('membre.inscription.error_nombre_seances_invalid');
            }
        } else if (stepKey === 'address') {
            if (addressAllRequired) {
                if (!form.adresse_correspondance.trim()) e.adresse_correspondance = t('membre.inscription.error_adresse_required');
                if (!form.npa_correspondance.trim()) e.npa_correspondance = t('membre.inscription.error_npa_required');
                if (!form.localite_correspondance.trim()) e.localite_correspondance = t('membre.inscription.error_localite_required');
                if (!form.pays_correspondance.trim()) e.pays_correspondance = t('membre.inscription.pays_required');
            } else if (addressCountryRequired) {
                const anyAddr = !!(
                    form.adresse_correspondance.trim()
                    || form.npa_correspondance.trim()
                    || form.localite_correspondance.trim()
                );
                if (anyAddr && !form.pays_correspondance.trim()) {
                    e.pays_correspondance = t('membre.inscription.pays_required');
                }
            }
        } else if (stepKey === 'engagement') {
            if (cotisationEnabled && !form.accepte_cotisation_statuts) {
                e.accepte_cotisation_statuts = t('membre.inscription.error_cotisation_required');
            }
        } else if (stepKey === 'summary') {
            // Récap : pas de champs propres ; l'étape n'est « complète » que si
            // toutes les étapes de contenu précédentes le sont (sinon le timeline
            // affiche le récap coché dès le chargement).
            const priorKeys = regStepKeys.filter(function (k) {
                return k !== 'summary' && k !== 'validation';
            });
            for (let i = 0; i < priorKeys.length; i++) {
                const priorErrors = stepFieldErrors(priorKeys[i], form);
                if (Object.keys(priorErrors).length > 0) {
                    e._summary_incomplete = '1';
                    break;
                }
            }
        } else if (stepKey === 'validation') {
            const abErr = antiBotError(form.math_answer);
            if (abErr) e.math_answer = abErr;
        }
        return e;
    }

    /**
     * Complétude frise : (1) l'utilisateur a déjà atteint l'étape, (2) pas d'erreur
     * de validation. Aligné sur le wizard profil (complétude = contenu / passage
     * réel), pas sur « zéro erreur sur un formulaire encore vide ».
     */
    function regStepComplete(stepKey: string): boolean {
        if (!visitedRegSteps.has(stepKey)) {
            return false;
        }
        return Object.keys(stepFieldErrors(stepKey, registrationForm)).length === 0;
    }

    function regStepOptional(stepKey: string): boolean {
        if (stepKey === 'address') {
            return addressOptional;
        }
        if (stepKey === 'contact') {
            return phoneOptional;
        }
        return false;
    }

    function regStepWrapClass(stepKey: string): string {
        return 'gas-reg-step' + (regStepKeys[regStep] === stepKey ? '' : ' gas-reg-step--hidden');
    }

    function goRegNext() {
        const stepKey = regStepKeys[regStep];
        const e = stepFieldErrors(stepKey, registrationForm);
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setErrors({});
        setRegStep(function (s) { return Math.min(s + 1, regStepKeys.length - 1); });
    }
    function goRegPrev() { setErrors({}); setRegStep(function (s) { return Math.max(0, s - 1); }); }
    function jumpRegStep(stepKey: string) {
        const i = regStepKeys.indexOf(stepKey);
        if (i >= 0) { setErrors({}); setRegStep(i); }
    }

    function validateVerificationForm(form: VerificationFormState = verificationForm): boolean {
        const nextErrors: FieldErrors = {};

        if (!form.genre) {
            nextErrors.genre = t('membre.inscription.error_genre_required');
        }
        if (form.en_formation_base === '') {
            nextErrors.en_formation_base = t('membre.inscription.error_formation_base_required');
        }
        const abErr = antiBotError(form.math_answer);
        if (abErr) {
            nextErrors.math_answer = abErr;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }

    function scrollToFirstError() {
        window.setTimeout(function () {
            const firstError = document.querySelector('.has-error, .error-message');
            if (!firstError) {
                return;
            }
            const field = firstError.closest('.aph-form-field') || firstError;
            const top = field.getBoundingClientRect().top + window.pageYOffset - 80;
            window.scrollTo({ top, behavior: 'auto' });
        }, 0);
    }

    function scrollToSuccess() {
        window.setTimeout(function () {
            const target = appRef.current || successRef.current;
            if (!target) {
                window.scrollTo({ top: 0, behavior: 'auto' });
                return;
            }
            const top = target.getBoundingClientRect().top + window.pageYOffset - 40;
            window.scrollTo({ top, behavior: 'auto' });
        }, 0);
    }

    function submitRegistration(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setGlobalError('');
        const nextForm = { ...registrationForm };

        if (nextForm.en_formation_base === '1') {
            const today = todayLocalDate();
            nextForm.pratique_depuis = today;
            nextForm.date_nombre_seances = today;
            nextForm.nombre_seances_donnees = 0;
        }

        if (nextForm.en_formation_base === '0') {
            if (!nextForm.pratique_depuis) {
                nextForm.pratique_depuis = todayLocalDate();
            }
            if (!nextForm.date_nombre_seances) {
                nextForm.date_nombre_seances = todayLocalDate();
            }
            if (nextForm.nombre_seances_donnees === '' || Number.isNaN(Number(nextForm.nombre_seances_donnees))) {
                nextForm.nombre_seances_donnees = 0;
            }
        }

        setRegistrationForm(nextForm);

        if (!validateRegistrationForm(nextForm)) {
            setGlobalError(t('membre.inscription.error_form_errors'));
            scrollToFirstError();
            return;
        }

        registrationMutation.mutate(nextForm);
    }

    function submitVerification(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setGlobalError('');

        if (!validateVerificationForm(verificationForm)) {
            setGlobalError(t('membre.inscription.error_form_errors'));
            scrollToFirstError();
            return;
        }

        verificationMutation.mutate(verificationForm);
    }

    if (registrationSuccess) {
        const successTitleKey = isImmediateMode
            ? 'membre.inscription.success_title_immediate'
            : isApprovalMode
                ? 'membre.inscription.success_title_approval'
                : 'membre.inscription.success_title';
        const successBodyKey = !isLoggedIn
            ? (
                !successEmailSent
                    ? (
                        successEmailQueued
                            ? 'membre.inscription.success_account_created_email_queued'
                            : 'membre.inscription.success_account_created_no_email'
                    )
                    : isImmediateMode
                        ? 'membre.inscription.success_account_created_immediate'
                        : isApprovalMode
                            ? 'membre.inscription.success_account_created_approval'
                            : 'membre.inscription.success_account_created'
            )
            : 'membre.inscription.success_account_migrated';

        return (
            <div className="aph-registration-container aph-registration-container--display-simple" ref={appRef}>
                <div className="aph-registration-success" ref={successRef}>
                    {config.logoUrl ? (
                        <div className="logo-aph-container">
                            <img src={config.logoUrl} alt="Logo" className="logo-aph" />
                        </div>
                    ) : null}
                    <h1>{t(successTitleKey)}</h1>
                    <div className="aph-success-message">
                        {!isLoggedIn ? (
                            <p>{t(successBodyKey)}</p>
                        ) : (
                            <p>
                                {t(successBodyKey)}
                                <br /><br />
                                {successProfileUrl ? (
                                    <strong>
                                        <a href={successProfileUrl}><i className="fas fa-arrow-right" /> {t('membre.inscription.success_fill_profile')}</a>
                                    </strong>
                                ) : null}
                            </p>
                        )}
                    </div>
                    <div className="aph-next-steps">
                        <h2>{t('membre.inscription.success_next_steps_title')}</h2>
                        <GasMembershipSteps steps={steps} currentStep={successCurrentStep} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="aph-registration-container aph-registration-container--display-simple" ref={appRef}>
            <div className="aph-registration-header">
                {config.logoUrl ? (
                    <div className="logo-aph-container">
                        <img src={config.logoUrl} alt="Logo" className="logo-aph" />
                    </div>
                ) : null}
                <h1>{t('membre.inscription.form_title')}</h1>
            </div>

            {globalError ? <div className="aph-error-messages">{globalError}</div> : null}

            <p className="gas-explication-nouveau-membre-intro">
                {t(
                    isImmediateMode
                        ? 'membre.inscription.reg_intro_immediate'
                        : isApprovalMode
                            ? 'membre.inscription.reg_intro_approval'
                            : 'membre.inscription.reg_intro',
                )}
            </p>

            <ul className="aph-registration-highlights">
                <li className="aph-registration-highlight">
                    <span className="aph-registration-highlight__icon" aria-hidden="true">⏱️</span>
                    {t(
                        isSimpleMode
                            ? 'membre.inscription.reg_intro_highlight_time_simple'
                            : 'membre.inscription.reg_intro_highlight_time',
                    )}
                </li>
                {!isImmediateMode && membershipMode === 'certificate' ? (
                    <li className="aph-registration-highlight">
                        <span className="aph-registration-highlight__icon" aria-hidden="true">📄</span>
                        {t('membre.inscription.reg_intro_highlight_files')}
                    </li>
                ) : null}
                <li className="aph-registration-highlight">
                    <span className="aph-registration-highlight__icon" aria-hidden="true">🧭</span>
                    {t('membre.inscription.reg_intro_highlight_guided')}
                </li>
                {isImmediateMode ? (
                    <li className="aph-registration-highlight">
                        <span className="aph-registration-highlight__icon" aria-hidden="true">✅</span>
                        {t('membre.inscription.reg_intro_highlight_active')}
                    </li>
                ) : null}
            </ul>

            <div className="aph-registration-process">
                <h2>{t('membre.inscription.process_title')}</h2>
                <GasMembershipSteps steps={steps} currentStep={1} />
            </div>

            {hasMemberAccount ? (
                <div className="aph-already-logged">
                    <p><strong>{t('membre.inscription.already_member')}</strong></p>
                    {existingMemberEditUrl ? (
                        <p>
                            {t('membre.inscription.already_member_edit_url')}{' '}
                            <a href={existingMemberEditUrl}>{existingMemberEditUrl}</a>
                        </p>
                    ) : null}
                </div>
            ) : null}

            {isLoggedIn && !hasMemberAccount ? (
                <div className="aph-already-logged">
                    <div className="aph-user-info">
                        <h2>{t('membre.inscription.logged_in_title')}</h2>
                        <p>{t('membre.inscription.logged_in_desc')}</p>
                        <p><strong>{t('membre.inscription.logged_in_prenom')}</strong> {userInfo.firstName}</p>
                        <p><strong>{t('membre.inscription.logged_in_nom')}</strong> {userInfo.lastName}</p>
                        <p><strong>{t('membre.inscription.logged_in_email')}</strong> {userInfo.email}</p>
                    </div>

                    <div className="aph-verification-message">
                        <p>{t('membre.inscription.migrate_desc')}</p>
                        <form onSubmit={submitVerification} noValidate>
                            <div className="aph-form-grid">
                                <div className={'aph-form-field' + (errors.genre ? ' has-error' : '')}>
                                    <RegFieldLabel htmlFor="genre" label={t('membre.inscription.reg_genre_label')} required />
                                    <GasSelect
                                        id="genre"
                                        value={verificationForm.genre}
                                        onValueChange={function (value) { updateVerificationField('genre', value); }}
                                        options={genreOptions}
                                        placeholder={selectPlaceholder}
                                    />
                                    <FormError message={errors.genre} />
                                </div>

                                <div className={'aph-form-field' + (errors.en_formation_base ? ' has-error' : '')}>
                                    <RegFieldLabel htmlFor="verif-en-formation" label={texts.formationBaseLabel} required />
                                    <GasSelect
                                        id="verif-en-formation"
                                        value={verificationForm.en_formation_base}
                                        onValueChange={function (value) { updateVerificationField('en_formation_base', value); }}
                                        options={verificationFormationOptions}
                                        placeholder={selectPlaceholder}
                                    />
                                    {verificationForm.en_formation_base === '1' ? (
                                        <div className="aph-field-info-message"><p>{t('membre.inscription.reg_formation_base_oui_info_verif')}</p></div>
                                    ) : null}
                                    <FormError message={errors.en_formation_base} />
                                </div>

                                {turnstileActive ? (
                                    <div className={'aph-form-field aph-form-field--full gas-registration-turnstile-field' + (errors.math_answer ? ' has-error' : '')}>
                                        <div className="gas-react-anti-spam__honeypot" aria-hidden="true" tabIndex={-1}>
                                            <label htmlFor="verif-website">{t('membre.inscription.reg_honeypot_label')}</label>
                                            <input type="text" id="verif-website" value={verificationForm.website} autoComplete="off" tabIndex={-1} onChange={function (event) { updateVerificationField('website', event.target.value); }} />
                                        </div>
                                        <RegFieldLabel className="gas-registration-turnstile-field__label" label={t('membre.inscription.turnstile_label')} required />
                                        <GasTurnstileField
                                            siteKey={config.turnstile?.site_key || ''}
                                            action={config.turnstile?.action || 'member_registration'}
                                            onToken={handleTurnstileToken}
                                            onUnavailable={function () { setTurnstileUnavailable(true); }}
                                            className="gas-registration-turnstile"
                                            ariaLabel={t('membre.inscription.turnstile_label')}
                                        />
                                        {turnstileUnavailable ? <p className="gas-explication-partie-formulaire gas-registration-map-warning">{t('membre.inscription.error_turnstile_unavailable')}</p> : null}
                                        <FormError message={errors.math_answer} />
                                    </div>
                                ) : (
                                    <GasAntiSpamField
                                        honeypotId="verif-website"
                                        honeypotLabel={t('membre.inscription.reg_honeypot_label')}
                                        honeypotValue={verificationForm.website}
                                        onHoneypotChange={function (value) { updateVerificationField('website', value); }}
                                        challengeId="verif-math"
                                        challengeLabel={t('membre.inscription.reg_math_label', { a: config.mathA || 0, b: config.mathB || 0 })}
                                        challengeValue={String(verificationForm.math_answer)}
                                        onChallengeChange={function (value) { updateVerificationField('math_answer', value); }}
                                        error={errors.math_answer}
                                        disabled={isSubmitting}
                                        className="aph-form-field aph-form-field--full"
                                    />
                                )}
                            </div>

                            <button type="submit" className="gas-button gas-button--success" disabled={isSubmitting}>
                                {isSubmitting ? t('membre.inscription.verif_submitting') : t('membre.inscription.verif_submit_btn')}
                            </button>
                        </form>
                    </div>

                    <div className="aph-logout-section">
                        <p>{t('membre.inscription.logout_hint')}</p>
                        <a href={config.logoutUrl || '#'} className="gas-button gas-button--danger">{t('membre.inscription.logout_btn')}</a>
                    </div>
                </div>
            ) : null}

            {!isLoggedIn && !hasMemberAccount ? (
                <div className="aph-registration-form">
                    <a id="formulaire" />
                    <h2>{t('membre.inscription.reg_form_title')}</h2>
                    <h4>{t('membre.inscription.reg_form_required_badge_hint')}</h4>

                    <div className="gas-reg-wizard">
                        <aside className="gas-reg-wizard__sidebar">
                            <GasStepTimeline
                                steps={regStepKeys.map(function (k): GasStepTimelineStep {
                                    const done = regStepComplete(k);
                                    return {
                                        key: k,
                                        label: t('membre.inscription.wizard_step_' + k),
                                        complete: done,
                                        percent: done ? 100 : 0,
                                        optional: regStepOptional(k),
                                    };
                                })}
                                overallPercent={Math.round(regStepKeys.filter(regStepComplete).length / regStepKeys.length * 100)}
                                activeKey={regStepKeys[regStep]}
                                onStepClick={jumpRegStep}
                                summaryLabel={t('membre.inscription.wizard_progress', { percent: String(Math.round(regStepKeys.filter(regStepComplete).length / regStepKeys.length * 100)) })}
                            />
                        </aside>
                    <div className="gas-reg-wizard__main">
                    <RegStepGuidance stepKey={regStepKeys[regStep]} />

                    <form onSubmit={submitRegistration} noValidate>
                        <div className="aph-form-grid">
                            <div className={regStepWrapClass('identity')}>
                            <div className={'aph-form-field' + (errors.prenom ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="prenom" label={t('membre.profile.field_prenom')} required help={t('membre.inscription.reg_prenom_hint')} />
                                <input type="text" id="prenom" value={registrationForm.prenom} required onChange={function (event) { updateRegistrationField('prenom', event.target.value); }} />
                                <FormError message={errors.prenom} />
                            </div>

                            <div className={'aph-form-field' + (errors.nom ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="nom" label={t('membre.profile.field_nom')} required help={t('membre.inscription.reg_nom_hint')} />
                                <input type="text" id="nom" value={registrationForm.nom} required onChange={function (event) { updateRegistrationField('nom', event.target.value); }} />
                                <FormError message={errors.nom} />
                            </div>

                            <div className={'aph-form-field' + (errors.genre ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="reg-genre" label={t('membre.inscription.reg_genre_label')} required />
                                <GasSelect
                                    id="reg-genre"
                                    value={registrationForm.genre}
                                    onValueChange={function (value) { updateRegistrationField('genre', value); }}
                                    options={genreOptions}
                                    placeholder={selectPlaceholder}
                                />
                                <FormError message={errors.genre} />
                            </div>

                            <div className={'aph-form-field' + (errors.email || emailCheckStatus === 'taken' ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="email" label={t('membre.inscription.reg_email_label')} required />
                                <input type="email" id="email" value={registrationForm.email} required autoComplete="email" onChange={function (event) { updateRegistrationField('email', event.target.value); }} />
                                {emailCheckStatus === 'checking' ? (
                                    <p className="gas-reg-email-status gas-reg-email-status--checking">{t('membre.inscription.email_check_checking')}</p>
                                ) : null}
                                {emailCheckStatus === 'ok' ? (
                                    <p className="gas-reg-email-status gas-reg-email-status--ok">{t('membre.inscription.email_check_ok')}</p>
                                ) : null}
                                <FormError message={errors.email} />
                            </div>
                            </div>{/* /step identity */}

                            {!isSimpleMode ? (
                            <>
                            <div className={regStepWrapClass('contact')}>
                            <div className={'aph-form-field' + (errors.date_naissance ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="date-naissance" label={t('membre.inscription.reg_date_naissance_label')} />
                                <GasDateField
                                    id="date-naissance"
                                    value={registrationForm.date_naissance}
                                    onValueChange={function (value) { updateRegistrationField('date_naissance', value); }}
                                />
                                <FormError message={errors.date_naissance} />
                            </div>

                            <div className={'aph-form-field' + (errors.telephone ? ' has-error' : '')}>
                                <RegFieldLabel
                                    htmlFor="telephone"
                                    label={t('membre.inscription.reg_telephone_label')}
                                    required={!phoneOptional}
                                    help={phoneOptional
                                        ? t('membre.inscription.reg_telephone_hint_optional')
                                        : t('membre.inscription.reg_telephone_hint')}
                                />
                                <GasPhoneField
                                    id="telephone"
                                    value={registrationForm.telephone}
                                    required={!phoneOptional}
                                    onValueChange={function (value) { updateRegistrationField('telephone', value); }}
                                />
                                <FormError message={errors.telephone} />
                            </div>
                            </div>{/* /step contact */}

                            <div className={regStepWrapClass('practice')}>
                            <div className={'aph-form-field' + (errors.en_formation_base ? ' has-error' : '')}>
                                <RegFieldLabel htmlFor="reg-en-formation" label={texts.formationBaseLabel} required />
                                <GasSelect
                                    id="reg-en-formation"
                                    value={registrationForm.en_formation_base}
                                    onValueChange={function (value) { updateRegistrationField('en_formation_base', value); }}
                                    options={registrationFormationOptions}
                                    placeholder={selectPlaceholder}
                                />
                                {registrationForm.en_formation_base === '1' ? <div className="aph-field-info-message"><p>{t('membre.inscription.reg_formation_base_oui_info')}</p></div> : null}
                                {registrationForm.en_formation_base === '0' ? <div className="aph-field-info-message"><p>{t('membre.inscription.reg_formation_base_non_info')}</p></div> : null}
                                <FormError message={errors.en_formation_base} />
                            </div>

                            {registrationForm.en_formation_base === '0' ? (
                                <>
                                    <div className={'aph-form-field' + (errors.pratique_depuis ? ' has-error' : '')}>
                                        <RegFieldLabel htmlFor="pratique-depuis" label={t('membre.inscription.reg_pratique_depuis_label')} required help={t('membre.inscription.reg_pratique_depuis_hint')} />
                                        <GasDateField
                                            id="pratique-depuis"
                                            value={registrationForm.pratique_depuis}
                                            onValueChange={function (value) { updateRegistrationField('pratique_depuis', value); }}
                                            required
                                        />
                                        <FormError message={errors.pratique_depuis} />
                                    </div>
                                    <div className={'aph-form-field' + (errors.date_nombre_seances ? ' has-error' : '')}>
                                        <RegFieldLabel htmlFor="date-nombre-seances" label={t('membre.inscription.reg_date_seances_label')} required help={t('membre.inscription.reg_date_seances_hint')} />
                                        <GasDateField
                                            id="date-nombre-seances"
                                            value={registrationForm.date_nombre_seances}
                                            onValueChange={function (value) { updateRegistrationField('date_nombre_seances', value); }}
                                            required
                                        />
                                        <FormError message={errors.date_nombre_seances} />
                                    </div>
                                    <div className={'aph-form-field' + (errors.nombre_seances_donnees ? ' has-error' : '')}>
                                        <RegFieldLabel htmlFor="nombre-seances" label={t('membre.inscription.reg_nombre_seances_label')} required help={t('membre.inscription.reg_nombre_seances_hint')} />
                                        <input type="number" min="0" id="nombre-seances" value={registrationForm.nombre_seances_donnees} required onChange={function (event) { updateRegistrationField('nombre_seances_donnees', event.target.value); }} />
                                        <FormError message={errors.nombre_seances_donnees} />
                                    </div>
                                </>
                            ) : null}
                            </div>{/* /step practice */}

                            {addressStepVisible ? (
                            <div className={regStepWrapClass('address')}>
                            <div className={'aph-form-field aph-form-field--full gas-registration-address-card' + (errors.adresse_correspondance || errors.npa_correspondance || errors.localite_correspondance || errors.latitude_correspondance || errors.longitude_correspondance ? ' has-error' : '')} data-gas-address-scope="1">
                                <RegFieldLabel
                                    htmlFor="gas-address-street"
                                    label={t('membre.inscription.reg_adresse_label')}
                                    required={addressAllRequired}
                                    help={addressOptional
                                        ? t('membre.inscription.reg_adresse_optional_hint')
                                        : t('membre.inscription.reg_adresse_begin_hint')}
                                />
                                {addressOptional ? (
                                    <p className="gas-explication-partie-formulaire">{t('membre.inscription.reg_adresse_later_note')}</p>
                                ) : null}
                                <p className="gas-explication-partie-formulaire gas-geo-info-message">{geoInfoMessage}</p>

                                {/* Autocomplete + mini-carte sans clé (providers officiels +
                                    OpenFreeMap) : remplace l'ancien couple input custom +
                                    helper impératif window.GasMapsHelper. */}
                                <GasAddressField
                                    value={{
                                        address: registrationForm.adresse_correspondance,
                                        npa: registrationForm.npa_correspondance,
                                        localite: registrationForm.localite_correspondance,
                                        pays: registrationForm.pays_correspondance,
                                        latitude: registrationForm.latitude_correspondance,
                                        longitude: registrationForm.longitude_correspondance,
                                    }}
                                    onChange={onAddressChange}
                                    apiKey=""
                                    countries={showPaysSelect ? geoCountries : undefined}
                                    searchCountries={geoCountries.map(function (country) { return country.code; })}
                                    defaultCountry={geoDefault}
                                    showMap={config.mapsEnabled !== false}
                                    disabled={isSubmitting}
                                />
                                <FormError message={errors.adresse_correspondance || errors.npa_correspondance || errors.localite_correspondance || errors.pays_correspondance} />
                                <FormError message={errors.latitude_correspondance || errors.longitude_correspondance} />
                            </div>
                            </div>
                            ) : null}
                            {/* /step address */}

                            {cotisationEnabled ? (
                            <div className={regStepWrapClass('engagement')}>
                            <div className={'aph-form-field aph-form-field--full gas-registration-engagement' + (errors.accepte_cotisation_statuts ? ' has-error' : '')}>
                                <div className="gas-registration-engagement__box">
                                    <label className="gas-registration-engagement__label">
                                        <input type="checkbox" checked={registrationForm.accepte_cotisation_statuts} className="gas-registration-engagement__checkbox" onChange={function (event) { updateRegistrationField('accepte_cotisation_statuts', event.target.checked); }} />
                                        <span className="gas-registration-engagement__text"><strong>{t('membre.inscription.reg_engagement_label')}</strong></span>
                                    </label>
                                    <p className="gas-registration-engagement__note">{t('membre.inscription.reg_engagement_note')}</p>
                                </div>
                                <FormError message={errors.accepte_cotisation_statuts} />
                            </div>
                            </div>
                            ) : null}
                            {/* /step engagement */}
                            </>
                            ) : null}

                            <div className={regStepWrapClass('summary')}>
                                <div className="aph-form-field aph-form-field--full gas-reg-summary">
                                    <h3 className="gas-reg-summary__title">{t('membre.inscription.summary_title')}</h3>
                                    <p className="gas-reg-summary__intro">{t('membre.inscription.summary_intro')}</p>
                                    <dl className="gas-reg-summary__list">
                                        <div className="gas-reg-summary__row">
                                            <dt>{t('membre.profile.field_prenom')}</dt>
                                            <dd>{registrationForm.prenom || '—'}</dd>
                                        </div>
                                        <div className="gas-reg-summary__row">
                                            <dt>{t('membre.profile.field_nom')}</dt>
                                            <dd>{registrationForm.nom || '—'}</dd>
                                        </div>
                                        <div className="gas-reg-summary__row">
                                            <dt>{t('membre.inscription.reg_email_label')}</dt>
                                            <dd>{registrationForm.email || '—'}</dd>
                                        </div>
                                        {!isSimpleMode ? (
                                            <div className="gas-reg-summary__row">
                                                <dt>{t('membre.inscription.reg_telephone_label')}</dt>
                                                <dd>{registrationForm.telephone || t('membre.inscription.summary_empty')}</dd>
                                            </div>
                                        ) : null}
                                        {!isSimpleMode && addressStepVisible ? (
                                            <div className="gas-reg-summary__row">
                                                <dt>{t('membre.inscription.reg_adresse_label')}</dt>
                                                <dd>
                                                    {(() => {
                                                        // Ne pas compter le pays par défaut seul comme « adresse saisie ».
                                                        const hasStreet = !!registrationForm.adresse_correspondance.trim();
                                                        const hasNpa = !!registrationForm.npa_correspondance.trim();
                                                        const hasCity = !!registrationForm.localite_correspondance.trim();
                                                        if (!hasStreet && !hasNpa && !hasCity) {
                                                            return t('membre.inscription.summary_empty');
                                                        }
                                                        return [
                                                            registrationForm.adresse_correspondance,
                                                            [registrationForm.npa_correspondance, registrationForm.localite_correspondance].filter(Boolean).join(' '),
                                                            (registrationForm.pays_correspondance || '').toUpperCase(),
                                                        ].filter(Boolean).join(', ');
                                                    })()}
                                                </dd>
                                            </div>
                                        ) : null}
                                    </dl>
                                    <p className="gas-reg-summary__hint">{t('membre.inscription.summary_edit_hint')}</p>
                                </div>
                            </div>{/* /step summary */}

                            <div className={regStepWrapClass('validation')}>
                            {turnstileActive ? (
                                <div className={'aph-form-field aph-form-field--full gas-registration-turnstile-field' + (errors.math_answer ? ' has-error' : '')}>
                                    <div className="gas-react-anti-spam__honeypot" aria-hidden="true" tabIndex={-1}>
                                        <label htmlFor="reg-website">{t('membre.inscription.reg_honeypot_label')}</label>
                                        <input type="text" id="reg-website" value={registrationForm.website} autoComplete="off" tabIndex={-1} onChange={function (event) { updateRegistrationField('website', event.target.value); }} />
                                    </div>
                                    <RegFieldLabel className="gas-registration-turnstile-field__label" label={t('membre.inscription.turnstile_label')} required />
                                    <GasTurnstileField
                                        siteKey={config.turnstile?.site_key || ''}
                                        action={config.turnstile?.action || 'member_registration'}
                                        onToken={handleTurnstileToken}
                                        onUnavailable={function () { setTurnstileUnavailable(true); }}
                                        className="gas-registration-turnstile"
                                        ariaLabel={t('membre.inscription.turnstile_label')}
                                    />
                                    {turnstileUnavailable ? <p className="gas-explication-partie-formulaire gas-registration-map-warning">{t('membre.inscription.error_turnstile_unavailable')}</p> : null}
                                    <FormError message={errors.math_answer} />
                                </div>
                            ) : (
                                <GasAntiSpamField
                                    honeypotId="reg-website"
                                    honeypotLabel={t('membre.inscription.reg_honeypot_label')}
                                    honeypotValue={registrationForm.website}
                                    onHoneypotChange={function (value) { updateRegistrationField('website', value); }}
                                    challengeId="reg-math"
                                    challengeLabel={t('membre.inscription.reg_math_label', { a: config.mathA || 0, b: config.mathB || 0 })}
                                    challengeValue={String(registrationForm.math_answer)}
                                    onChallengeChange={function (value) { updateRegistrationField('math_answer', value); }}
                                    error={errors.math_answer}
                                    disabled={isSubmitting}
                                    className="aph-form-field aph-form-field--full gas-registration-math-field"
                                />
                            )}
                            <p className="gas-reg-trust">
                                {t('membre.inscription.reg_trust_data')}
                                {privacyUrl ? (
                                    <>
                                        {' '}
                                        <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
                                            {t('membre.inscription.reg_trust_privacy_link')}
                                        </a>
                                    </>
                                ) : null}
                            </p>
                            </div>{/* /step validation */}
                        </div>

                        <div className="gas-reg-wizard__footer">
                            <button type="button" className="gas-button gas-button--secondary" disabled={regStep === 0} onClick={goRegPrev}>
                                {t('common.back')}
                            </button>
                            <div className="gas-reg-wizard__footer-right">
                                {regStep < regStepKeys.length - 1 ? (
                                    <button type="button" className="gas-button gas-button--primary" onClick={goRegNext}>
                                        {t('common.next')}
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="gas-button gas-button--primary"
                                        disabled={
                                            isSubmitting
                                            || (cotisationEnabled && !isSimpleMode && !registrationForm.accepte_cotisation_statuts)
                                            || emailCheckStatus === 'taken'
                                            || emailCheckStatus === 'checking'
                                        }
                                    >
                                        {isSubmitting ? t('membre.inscription.reg_submitting') : t('membre.inscription.reg_submit_btn')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                    </div>{/* /wizard main */}
                    </div>{/* /wizard */}
                </div>
            ) : null}
        </div>
    );
}
